import React from "react";
import PropTypes from "prop-types";
import { getStyled } from "@sf-core";
import { CardHeader } from "@mui/material";
import { Handle, Position } from "reactflow";
import { useTranslation } from "react-i18next";
import { StyledCard } from "./constants";
import SFTypography from "../../typography";
import SFButton from "../../button";
import { FLOW_CHART_NODE_TYPES } from "./constants";

const { styled } = getStyled();

const StyledChildrenContainer = styled("div", { shouldForwardProp: false })(
  ({ theme }) => ({
    display: "flex",
    alignItems: "center",
    position: "absolute",
    bottom: "-10px",
    width: "100%",
  })
);

const StyledChildrenButton = styled(SFButton, {
  shouldForwardProp: (prop) => prop !== "disableExpand",
})(({ theme, disableExpand, variant }) => {
  let themeVariantStyles = theme.components?.MuiButton?.variants?.find(
    (x) => x.props.variant === variant
  )?.style;
  return {
    padding: "2px 6px",
    margin: "0 auto",
    borderRadius: "16px",
    ...(disableExpand && {
      "&:hover": {
        backgroundColor:
          themeVariantStyles?.backgroundColor ?? theme.palette.grey100.main,
        color: themeVariantStyles?.color ?? theme.palette.grey500.main,
      },
    }),
  };
});

const SFTypographySubheaderStyled = styled(SFTypography)(({ theme }) => ({
  height: 15,
}));

const CustomNode = (props) => {
  const { id, type, data, toggleChildren, ...rest } = props;
  const { t } = useTranslation();

  let showEmptyChildrenCount =
    rest.showEmptyChildrenCount &&
    rest.showEmptyChildrenCount({ id, type, data });

  const toggleChildrenHandler = () => {
    if (data.childrenCount > 0 && !rest.disableExpand) {
      toggleChildren(id);
    }
  };

  return (
    <>
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <StyledCard hasError={!data.label} elevation={0} disabled={data.disabled}>
        <CardHeader
          disableTypography
          avatar={rest.getIcon && rest.getIcon({ id, type, data })}
          title={
            <SFTypography
              variant="body1bold"
              {...(data.disabled && { color: "grey350" })}
            >
              {data.title}
            </SFTypography>
          }
          {...(!data.disabled && {
            subheader: (
              <SFTypographySubheaderStyled
                variant="body2"
                hasHtml
                className="max-2-lines"
              >
                {data.label ||
                  t("components.flowChart.questions.missingContent")}
              </SFTypographySubheaderStyled>
            ),
          })}
          {...(rest.getActions && {
            action: rest.getActions({ id, type, data }),
          })}
        />
      </StyledCard>
      {(data.childrenCount > 0 || showEmptyChildrenCount) && (
        <StyledChildrenContainer>
          <StyledChildrenButton
            variant="utility"
            disableExpand={rest.disableExpand}
            onClick={toggleChildrenHandler}
            {...(rest.getExpandIcon &&
              !rest.disableExpand && {
              endIcon: rest.getExpandIcon({ id, type, data }),
            })}
          >
            {rest.getChildrenLabel && rest.getChildrenLabel({ id, type, data })}
          </StyledChildrenButton>
        </StyledChildrenContainer>
      )}
      <Handle
        type="source"
        position={Position.Bottom}
        id="a"
        style={{ opacity: 0 }}
      />
    </>
  );
};

CustomNode.propTypes = {
  /**the node id */
  id: PropTypes.string.isRequired,
  /**The type of the node */
  type: PropTypes.oneOf(Object.values(FLOW_CHART_NODE_TYPES)).isRequired,
  /**The data of the node */
  data: PropTypes.object.isRequired,
  /**Function to toggle the children */
  toggleChildren: PropTypes.func.isRequired,
  /**Function indicator to display the children count pill button if node hasn't children */
  showEmptyChildrenCount: PropTypes.func,
  /**Function to get the icon displayed on the left of the node */
  getIcon: PropTypes.func,
  /**Function to get the actions displayed on the right of the node */
  getActions: PropTypes.func,
  /**Function to get the expand icon displayed on the right of the children count pill button*/
  getExpandIcon: PropTypes.func,
  /**Function to get the label displayed on the children button */
  getChildrenLabel: PropTypes.func,
  /**Property indicator to disable  the expand functionality*/
  disableExpand: PropTypes.bool,
};

export default CustomNode;
